import { useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useAgentStore } from '../lib/agent-store';
import type { CreateAgentInput, UpdateAgentInput } from '../lib/agent-types';

export function useAgents() {
  const { agents, loading, error, setAgents, setLoading, setError, addAgent, updateAgent, removeAgent } = useAgentStore();

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await trpc.agent.list.query();
      setAgents(result as any);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  }, [setAgents, setLoading, setError]);

  const fetchPublished = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.agent.listPublished.query();
      setAgents(result as any);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setAgents, setLoading, setError]);

  const createAgent = useCallback(async (input: CreateAgentInput) => {
    setLoading(true);
    try {
      const agent = await trpc.agent.create.mutate(input);
      addAgent(agent as any);
      return agent;
    } catch (err: any) {
      setError(err.message || 'Failed to create agent');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addAgent, setLoading, setError]);

  const editAgent = useCallback(async (id: string, data: UpdateAgentInput) => {
    try {
      const updated = await trpc.agent.update.mutate({ id, data });
      updateAgent(id, data);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update agent');
      throw err;
    }
  }, [updateAgent, setError]);

  const deleteAgent = useCallback(async (id: string) => {
    try {
      await trpc.agent.delete.mutate({ id });
      removeAgent(id);
    } catch (err: any) {
      setError(err.message || 'Failed to delete agent');
      throw err;
    }
  }, [removeAgent, setError]);

  const publishAgent = useCallback(async (id: string) => {
    try {
      const updated = await trpc.agent.publish.mutate({ id });
      updateAgent(id, { published: true } as any);
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [updateAgent, setError]);

  const unpublishAgent = useCallback(async (id: string) => {
    try {
      const updated = await trpc.agent.unpublish.mutate({ id });
      updateAgent(id, { published: false } as any);
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [updateAgent, setError]);

  return {
    agents,
    loading,
    error,
    fetchAgents,
    fetchPublished,
    createAgent,
    editAgent,
    deleteAgent,
    publishAgent,
    unpublishAgent,
  };
}
